import TextButton from '@shared/components/core/button/TextButton';
import ContentDialog from '@shared/components/core/dialog/ContentDialog';
import { BodyMedium } from '@shared/components/core/text/Typography';
import { useAlertsStore } from '@shared/hooks/state/AlertsContainer';
import { useOrganizationStore } from '@shared/hooks/state/OrganizationContainer';
import OrganizationMemberModel from '@shared/models/organizations/OrganizationMemberModel';
import OrganizationMemberRole from '@shared/models/organizations/OrganizationMemberRole';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import MemberDetailStack from './MemberDetailStack';
import MemberEditDialog from './MemberEditDialog';

interface IMemberEditAccessProps {
    myRole: OrganizationMemberRole;
    member: OrganizationMemberModel;
    organizationId: string;
}

export default function MemberEditAccess({ myRole, member, organizationId }: Readonly<IMemberEditAccessProps>) {
    const { t } = useTranslation();

    const [showEditDialog, setShowEditDialog] = useState<boolean>(false);
    const [showRemoveDialog, setShowRemoveDialog] = useState<boolean>(false);

    const handleEdit = () => {
        setShowEditDialog(true);
    };

    const handleCancelEdit = () => {
        setShowEditDialog(false);
    };

    const handleRemove = () => {
        setShowRemoveDialog(true);
    };

    const handleCancelRemove = () => {
        setShowRemoveDialog(false);
    };

    const handleConfirmRemoveMember = async () => {
        if (organizationId && (myRole.canRemoveMembers || myRole.isAdmin) && member.id) {
            const result = await useOrganizationStore.getState().removeMember(organizationId, member.id);
            if (result?.resultType == 'Ok') {
                useAlertsStore.getState().showSuccess(
                    t('common:organization.member.delete', 'Member removed successfully', {
                        value: member.user?.email,
                    })
                );
            } else {
                useAlertsStore.getState().showError(
                    t('common:organization.member.deleteError', 'Error deleting member', {
                        value: member.user?.email,
                    })
                );
            }
        }
        setShowRemoveDialog(false);
    };

    if (!member || !myRole) return null;
    return (
        <>
            {(myRole.canChangeMemberPermissions || myRole.isAdmin) && (
                <>
                    <TextButton startIcon="orgPermissions" onClick={() => handleEdit()}>
                        {t('common:edit')}
                    </TextButton>
                    <MemberEditDialog
                        myRole={myRole}
                        open={showEditDialog}
                        member={member}
                        onClose={handleCancelEdit}
                    />
                </>
            )}
            {(myRole.canRemoveMembers || myRole.isAdmin) && (
                <>
                    <TextButton variant="negative" startIcon="cancel2" onClick={handleRemove}>
                        {t('common:organization.canRemoveMembers.action')}
                    </TextButton>
                    <ContentDialog
                        open={showRemoveDialog}
                        onConfirm={handleConfirmRemoveMember}
                        onCancel={handleCancelRemove}
                        onClose={handleCancelRemove}
                        title={t('common:organization.canRemoveMembers.confirm')}
                        confirmButtonText={t('common:organization.canRemoveMembers.titleSingle')}>
                        <MemberDetailStack member={member} />
                        <BodyMedium>{t('common:organization.canRemoveMembers.confirmLong')}</BodyMedium>
                    </ContentDialog>
                </>
            )}
        </>
    );
}
